import { apiService } from './apiService';

// Types aligned with backend enhanced inventory views
export interface DetailedStockItem {
  id: number;
  product: {
    id: number;
    name: string;
    code: string;
    barcode?: string | null;
    cost_price?: number | null;
    selling_price?: number | null;
    unit_of_measure?: string;
    reorder_level?: number;
    category?: string | null;
  };
  location: {
    id: number;
    name: string;
    code: string;
  };
  quantities: {
    on_hand: number;
    committed: number;
    available: number;
  };
  last_updated: string;
  stock_status: 'Low Stock' | 'Normal';
  manufacture_date?: string | null;
  expiry_date?: string | null;
  expiry_alert_days?: number;
  days_to_expiry?: number | null;
  expiry_status?: 'expired' | 'expiring_soon' | 'ok' | 'no_date';
  expiry_label?: string;
  recent_receipts?: Array<{
    grpo_number: string;
    received_date: string;
    received_quantity: number;
    unit_price: number;
    po_number: string;
  }>;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
}

export interface MovementItem {
  id: number;
  product: { id: number; name: string; code: string; unit_of_measure?: string };
  location: { id: number; name: string; code: string };
  movement_details: {
    type: string; // IN/OUT
    quantity: number;
    unit_cost?: number | null;
    reference_type: string;
    reference_id: number;
  };
  created_by: { id: number; name: string; username: string };
  created_at: string;
  notes?: string | null;
  reference_details?: any;
}

export interface InventoryDashboard {
  stock_summary: {
    total_products: number;
    total_stock_units: number;
    low_stock_products: number;
    out_of_stock_products: number;
    expired_products?: number;
    expiring_soon_products?: number;
  };
  movements_summary: {
    recent_movements_in: number;
    recent_movements_out: number;
    net_movement: number;
  };
  pending_actions: {
    pending_adjustments: number;
    pending_transfers: number;
    recent_goods_received: number;
  };
  location_info: {
    location_id?: number | null;
    location_name: string;
    scope?: 'single_location' | 'all_locations';
  };
}

export interface StockAdjustmentCreatePayload {
  location_id: number;
  adjustment_date: string; // YYYY-MM-DD
  reason: string;
  notes?: string;
  items: Array<{
    product_id: number;
    current_quantity: number;
    adjusted_quantity: number;
    reason?: string;
    manufacture_date?: string;
    expiry_date?: string;
  }>;
}

export interface StockAdjustmentListItem {
  id: number;
  adjustment_number: string;
  location_id: number;
  adjustment_date: string;
  reason: string;
  notes?: string | null;
  created_by: number;
  approved_by?: number | null;
  status: 'PENDING' | 'APPROVED' | 'CANCELLED';
  created_at?: string;
  updated_at?: string;
  adjustment_items?: Array<{
    id: number;
    product_id: number;
    current_quantity: number;
    adjusted_quantity: number;
    difference: number;
    reason?: string | null;
    product?: { id: number; name: string; code: string };
  }>;
}

export interface StockTransferListItem {
  id: number;
  transfer_number: string;
  from_location_id: number;
  to_location_id: number;
  transfer_date: string;
  transfer_type: 'BRANCH_TRANSFER' | 'DAMAGE' | 'DEFECT' | 'RECALL';
  notes?: string | null;
  sent_by: number;
  received_by?: number | null;
  status: 'PENDING' | 'IN_TRANSIT' | 'RECEIVED';
  created_at?: string;
  updated_at?: string;
  transfer_items?: Array<{
    id: number;
    transfer_id: number;
    product_id: number;
    quantity: number;
    received_quantity: number;
    notes?: string | null;
    product?: { id: number; name: string; code: string };
  }>;
}

export interface StockTransferCreatePayload {
  from_location_id: number;
  to_location_id: number;
  transfer_date: string; // YYYY-MM-DD
  transfer_type: 'BRANCH_TRANSFER' | 'DAMAGE' | 'DEFECT' | 'RECALL';
  notes?: string;
  items: Array<{
    product_id: number;
    quantity: number;
    notes?: string;
  }>;
}

export interface StockTransferLineQuantityPayload {
  item_id: number;
  quantity: number;
}

export interface StockTransferUpdatePayload {
  status?: 'PENDING' | 'IN_TRANSIT' | 'RECEIVED';
  notes?: string;
  received_by?: number;
  items?: StockTransferLineQuantityPayload[];
}

export const inventoryService = {
  async getDashboard(params?: { location_id?: number }): Promise<InventoryDashboard> {
    return apiService.get<InventoryDashboard>(`/inventory-views/dashboard/inventory-overview`, params);
  },

  async getDetailedStock(params?: {
    skip?: number;
    limit?: number;
    location_id?: number;
    product_id?: number;
    search?: string;
    low_stock_only?: boolean;
    expiry_status?: 'expired' | 'expiring_soon' | 'ok' | 'no_date';
    include_recent_receipts?: boolean;
  }): Promise<PaginatedResponse<DetailedStockItem>> {
    return apiService.get<PaginatedResponse<DetailedStockItem>>(`/inventory-views/stock/detailed`, params);
  },

  async getDetailedMovements(params: {
    skip?: number;
    limit?: number;
    location_id?: number;
    product_id?: number;
    movement_type?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<PaginatedResponse<MovementItem>> {
    return apiService.get<PaginatedResponse<MovementItem>>(`/inventory-views/movements/detailed`, params);
  },

  async getLowStockAlerts(params?: { location_id?: number; include_vendors?: boolean }): Promise<any> {
    return apiService.get<any>(`/inventory-views/alerts/low-stock`, params);
  },

  async createStockAdjustment(payload: StockAdjustmentCreatePayload): Promise<{ id?: number; adjustment_number?: string }> {
    return apiService.post(`/inventory/adjustments/`, payload);
  },

  async getStockAdjustments(params?: {
    skip?: number;
    limit?: number;
    location_id?: number;
    status?: 'PENDING' | 'APPROVED' | 'CANCELLED';
  }): Promise<StockAdjustmentListItem[]> {
    return apiService.get<StockAdjustmentListItem[]>(`/inventory/adjustments/`, params);
  },

  async approveStockAdjustment(adjustmentId: number): Promise<{ message: string; adjustment_number?: string }> {
    return apiService.put<{ message: string; adjustment_number?: string }>(
      `/inventory/adjustments/${adjustmentId}/approve`,
      {},
    );
  },

  async cancelStockAdjustment(adjustmentId: number): Promise<{ message: string; adjustment_number?: string }> {
    return apiService.put<{ message: string; adjustment_number?: string }>(
      `/inventory/adjustments/${adjustmentId}/cancel`,
      {},
    );
  },

  async getStockTransfers(params?: {
    skip?: number;
    limit?: number;
    status?: 'PENDING' | 'IN_TRANSIT' | 'RECEIVED';
    from_location_id?: number;
    to_location_id?: number;
  }): Promise<StockTransferListItem[]> {
    return apiService.get<StockTransferListItem[]>(`/inventory/transfers/`, params);
  },

  async createStockTransfer(payload: StockTransferCreatePayload): Promise<{ id?: number; transfer_number?: string }> {
    return apiService.post(`/inventory/transfers/`, payload);
  },

  async receiveStockTransfer(
    transferId: number,
    receivedItems: Record<number, number>,
  ): Promise<{ message: string; transfer_number?: string }> {
    return apiService.put<{ message: string; transfer_number?: string }>(
      `/inventory/transfers/${transferId}/receive`,
      receivedItems,
    );
  },

  async updateStockTransfer(
    transferId: number,
    payload: StockTransferUpdatePayload,
  ): Promise<StockTransferListItem> {
    return apiService.put<StockTransferListItem>(`/stock/transfers/${transferId}`, payload);
  },

  async deleteStockTransfer(transferId: number): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/stock/transfers/${transferId}`);
  },
};